import { AnimationStore } from 'component/Carousel/store/AnimationStore'
import { getAnimation } from 'component/Carousel/util/animate'
import { getBest } from 'component/Carousel/util/best'
import { attachDrag } from 'component/Carousel/util/drag'
import { attachResize } from 'component/Carousel/util/resize'
import { makeAutoObservable } from 'mobx'
import { omitUndefined } from 'util/object'

export type Settings = {
  // slide ratio
  ratio?: number
  // slide gap
  gap?: number
  // slide angle
  angle?: number
}

export const DEFAULTS: Required<Settings> = {
  ratio: 768 / 1344,
  gap: 24,
  angle: 0,
}

export class ElementStore {
  settings: Required<Settings>
  // slides root element
  root: HTMLElement
  // view under slides
  view: HTMLElement
  // main element containing view
  main: HTMLElement
  // current slide index
  index: number
  // number of slides
  count: number
  width: number = 0
  // slide height
  height: number = 0
  // position 0.5 means first element at center
  position: number = 0
  // linear left without scale
  left: number = 0

  private readonly animation = new AnimationStore()
  private readonly destructors = new Set<Function>()

  private drag: boolean = false

  constructor(root: HTMLElement, count: number, settings: Settings) {
    makeAutoObservable(this)
    this.settings = { ...DEFAULTS, ...omitUndefined(settings) }
    this.root = root
    this.view = this.root.parentElement!
    if (!this.view) throw new Error('no parent')
    this.main = this.view.parentElement!
    if (!this.main) throw new Error('no parent')
    this.index = 0
    this.count = count
    this.attach()
  }

  get stable(): boolean {
    return !this.drag && !this.animation.busy
  }

  get ratio(): number {
    return this.settings.ratio
  }

  get gap(): number {
    return this.settings.gap
  }

  get angle(): number {
    return this.settings.angle
  }

  private attach() {
    this.setCount(this.count)
    this.destructors.add(attachDrag(this))
    this.destructors.add(attachResize(this))
    this.setIndex(this.index)
  }

  destroy() {
    this.animation.stop()
    for (const destructor of this.destructors) {
      try {
        destructor()
      } catch (e) {
        console.error('destructor error', e)
      }
    }
    this.destructors.clear()
  }

  setIndex(index: number, smooth?: boolean) {
    const max = this.count - 1
    index = Math.max(0, Math.min(max, index))

    this.index = index
    const an = getAnimation(this, !!smooth)

    this.animation.next(an)
  }

  setCount(count: number) {
    this.count = count
    this.main.style.setProperty('--slide-count', count.toFixed())

    this.setIndex(this.index)
  }

  onStartDrag = () => {
    this.drag = true
    this.animation.stop()
  }

  onEndDrag = () => {
    this.drag = false
    const index = getBest(this)
    this.setIndex(index, true)
  }

  onResize = () => {
    const main = this.main

    const { ratio, gap } = this
    let { width, height } = main.getBoundingClientRect()

    width -= gap
    width = Math.min(width, height * ratio)
    height = width / ratio
    width += gap

    this.width = width
    this.height = height

    main.style.setProperty('--slide-width', width.toFixed(3) + 'px')
    main.style.setProperty('--slide-height', height.toFixed(3) + 'px')
    main.style.setProperty('--slide-gap', gap.toFixed(3) + 'px')

    this.setIndex(this.index)
  }
}
